import type { operations } from "@tamariba/api-client";

export type PreviewLayout =
  operations["getLayout"]["responses"][200]["content"]["application/json"];
export type PreviewRenderer = {
  preview(document: PreviewLayout["document"]): Promise<string>;
};

function abortable<T>(promise: Promise<T>, signal: AbortSignal) {
  return new Promise<T>((resolve, reject) => {
    const cancel = () => reject(signal.reason);
    if (signal.aborted) return cancel();
    signal.addEventListener("abort", cancel, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", cancel);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener("abort", cancel);
        reject(error);
      },
    );
  });
}

// The cache belongs to one mounted provider, never to a user/session globally.
// Always authorize/refetch the document before reusing a rendered revision.
export function createLayoutPreviewLoader(
  renderer: Promise<PreviewRenderer | null>,
  fetcher: typeof fetch = fetch,
) {
  const abort = new AbortController();
  const pending = new Map<
    string,
    { promise: Promise<string>; abort: AbortController; consumers: number }
  >();
  const images = new Map<string, string>();
  const queue: Array<() => void> = [];
  let active = 0;
  let bytes = 0;

  async function run(url: string, signal: AbortSignal) {
    signal.throwIfAborted();
    if (active >= 4)
      await new Promise<void>((resolve, reject) => {
        const resume = () => {
          signal.removeEventListener("abort", cancel);
          resolve();
        };
        const cancel = () => {
          const index = queue.indexOf(resume);
          if (index >= 0) queue.splice(index, 1);
          reject(signal.reason);
        };
        signal.addEventListener("abort", cancel, { once: true });
        queue.push(resume);
      });
    else active++;
    try {
      signal.throwIfAborted();
      // Fetch while the iframe is starting, instead of waiting for its scripts.
      const response = await fetcher(url, {
        cache: "no-store",
        signal: AbortSignal.any([signal, AbortSignal.timeout(15000)]),
      });
      signal.throwIfAborted();
      if (!response.ok) throw new Error("配置図を取得できませんでした。");
      const layout = (await response.json()) as PreviewLayout;
      signal.throwIfAborted();
      const key = `${url.split("?")[0]}:${layout.revision}`;
      const cached = images.get(key);
      if (cached !== undefined) {
        images.delete(key);
        images.set(key, cached);
        return cached;
      }
      const api = await abortable(renderer, signal);
      signal.throwIfAborted();
      if (!api) throw new Error("プレビューを準備できませんでした。");
      // Let input/paint run between synchronous SVG render jobs.
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      signal.throwIfAborted();
      const svg = await api.preview(layout.document);
      signal.throwIfAborted();
      // Bound both count and approximate UTF-16 memory (large diagrams vary).
      if (svg.length * 2 <= 4 * 1024 * 1024) {
        bytes -= (images.get(key)?.length ?? 0) * 2;
        images.delete(key);
        images.set(key, svg);
        bytes += svg.length * 2;
        while (images.size > 32 || bytes > 4 * 1024 * 1024) {
          const oldest = images.keys().next().value;
          if (oldest === undefined) break;
          bytes -= (images.get(oldest)?.length ?? 0) * 2;
          images.delete(oldest);
        }
      }
      return svg;
    } finally {
      const resume = queue.shift();
      if (resume) resume();
      else active--;
    }
  }

  return {
    load(url: string, signal?: AbortSignal) {
      if (signal?.aborted) return Promise.reject(signal.reason);
      let task = pending.get(url);
      if (!task) {
        const controller = new AbortController();
        const current = {
          promise: Promise.resolve(""),
          abort: controller,
          consumers: 0,
        };
        current.promise = run(
          url,
          AbortSignal.any([abort.signal, controller.signal]),
        ).finally(() => {
          if (pending.get(url) === current) pending.delete(url);
        });
        pending.set(url, current);
        task = current;
      }
      task.consumers++;
      if (!signal) return task.promise;
      const current = task;
      const cancel = () => {
        if (--current.consumers === 0) {
          current.abort.abort();
          if (pending.get(url) === current) pending.delete(url);
        }
      };
      signal.addEventListener("abort", cancel, { once: true });
      return abortable(current.promise, signal).finally(() =>
        signal.removeEventListener("abort", cancel),
      );
    },
    dispose() {
      abort.abort();
      images.clear();
      bytes = 0;
    },
  };
}
